import { FC, useEffect, useRef, useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Divider,
  Avatar,
  Button,
  Input,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  ContactEmergency,
  PhotoCamera,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { z, ZodType } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useProfilePersonalDetailsState,
  useProfilePersonalDetailsActions,
} from '../../../contexts/profilePersonalDetails.context';
import { IProfilePersonalDetailsApiResponse } from 'src/apis/profile/profilePersonalDetails.interfaces';
import { GENDER } from 'src/apis/users/users.interfaces';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from 'src/views/components/controlledComponents/ControlledSelectField.component';

type EditPersonalDetailsFormData = {
  basicDetails: {
    primaryDetails: {
      firstName: string;
      middleName?: string | null;
      lastName: string;
      gender: GENDER | 'select';
      profilePicture?: File | string | null;
      emergencyContact?: {
        firstName: string;
        middleName?: string | null;
        lastName: string;
        gender: GENDER | 'select';
        relationship: string;
        phone: {
          countryCode: string;
          phoneNumber: string;
          isVerified: boolean;
        };
        email: {
          email: string;
          isVerified: boolean;
        };
        address?: {
          address1: string;
          address2?: string | null;
          city: string;
          state?: string | null;
          country?: string | null;
          postalCode: string;
        } | null;
      } | null;
    };
    contactDetails: {
      email: {
        email: string;
        isVerified: boolean;
      };
      phone: {
        countryCode: string;
        phoneNumber: string;
        isVerified: boolean;
      };
    };
    addressDetails: {
      address1: string;
      address2?: string | null;
      city: string;
      state?: string | null;
      country?: string | null;
      postalCode: string;
    };
  };
};

const editPersonalDetailsSchema: ZodType<EditPersonalDetailsFormData> =
  z.object({
    basicDetails: z.object({
      primaryDetails: z.object({
        firstName: z
          .string()
          .min(2, 'First name must be at least 2 characters')
          .max(50, 'First name must not exceed 50 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'First name should only contain letters and spaces'
          ),
        middleName: z
          .string()
          .regex(
            /^[A-Za-z\s]*$/,
            'Middle name should only contain letters and spaces'
          )
          .max(50, 'Middle name must not exceed 50 characters')
          .nullable()
          .optional(),
        lastName: z
          .string()
          .min(2, 'Last name must be at least 2 characters')
          .max(50, 'Last name must not exceed 50 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'Last name should only contain letters and spaces'
          ),
        gender: z
          .nativeEnum(GENDER, {
            errorMap: () => ({ message: 'Please select a valid gender' }),
          })
          .or(z.literal('select')),
        profilePicture: z
          .union([z.instanceof(File), z.string(), z.null()])
          .optional(),
        emergencyContact: z
          .object({
            firstName: z
              .string()
              .min(2, 'First name must be at least 2 characters')
              .max(50, 'First name must not exceed 50 characters')
              .regex(
                /^[A-Za-z\s]+$/,
                'First name should only contain letters and spaces'
              ),
            middleName: z
              .string()
              .regex(
                /^[A-Za-z\s]*$/,
                'Middle name should only contain letters and spaces'
              )
              .max(50, 'Middle name must not exceed 50 characters')
              .nullable()
              .optional(),
            lastName: z
              .string()
              .min(2, 'Last name must be at least 2 characters')
              .max(50, 'Last name must not exceed 50 characters')
              .regex(
                /^[A-Za-z\s]+$/,
                'Last name should only contain letters and spaces'
              ),
            gender: z
              .nativeEnum(GENDER, {
                errorMap: () => ({ message: 'Please select a valid gender' }),
              })
              .or(z.literal('select')),
            relationship: z
              .string()
              .min(2, 'Relationship must be at least 2 characters')
              .max(50, 'Relationship must not exceed 50 characters'),
            phone: z.object({
              countryCode: z
                .string()
                .min(1, 'Country code is required')
                .max(4, 'Invalid country code')
                .regex(/^\+?\d{1,4}$/, 'Invalid country code format'),
              phoneNumber: z
                .string()
                .min(8, 'Phone number must be at least 8 digits')
                .max(15, 'Phone number must not exceed 15 digits')
                .regex(/^\d+$/, 'Phone number should only contain digits'),
              isVerified: z.boolean(),
            }),
            email: z.object({
              email: z
                .string()
                .email('Please enter a valid email address')
                .min(5, 'Email is required')
                .max(255, 'Email is too long'),
              isVerified: z.boolean(),
            }),
            address: z
              .object({
                address1: z
                  .string()
                  .min(5, 'Address must be at least 5 characters')
                  .max(200, 'Address must not exceed 200 characters'),
                address2: z
                  .string()
                  .max(200, 'Address must not exceed 200 characters')
                  .nullable()
                  .optional(),
                city: z
                  .string()
                  .min(2, 'City must be at least 2 characters')
                  .max(100, 'City must not exceed 100 characters')
                  .regex(
                    /^[A-Za-z\s]+$/,
                    'City should only contain letters and spaces'
                  ),
                state: z
                  .string()
                  .min(2, 'State must be at least 2 characters')
                  .max(100, 'State must not exceed 100 characters')
                  .regex(
                    /^[A-Za-z\s]+$/,
                    'State should only contain letters and spaces'
                  )
                  .nullable()
                  .optional(),
                country: z
                  .string()
                  .min(2, 'Country must be at least 2 characters')
                  .max(100, 'Country must not exceed 100 characters')
                  .regex(
                    /^[A-Za-z\s]+$/,
                    'Country should only contain letters and spaces'
                  )
                  .nullable()
                  .optional(),
                postalCode: z
                  .string()
                  .min(4, 'Postal code must be at least 4 characters')
                  .max(10, 'Postal code must not exceed 10 characters')
                  .regex(/^[A-Za-z0-9\s-]+$/, 'Invalid postal code format'),
              })
              .nullable()
              .optional(),
          })
          .nullable()
          .optional(),
      }),
      contactDetails: z.object({
        email: z.object({
          email: z
            .string()
            .email('Please enter a valid email address')
            .min(5, 'Email is required')
            .max(255, 'Email is too long'),
          isVerified: z.boolean(),
        }),
        phone: z.object({
          countryCode: z
            .string()
            .min(1, 'Country code is required')
            .max(4, 'Invalid country code')
            .regex(/^\+?\d{1,4}$/, 'Invalid country code format'),
          phoneNumber: z
            .string()
            .min(8, 'Phone number must be at least 8 digits')
            .max(15, 'Phone number must not exceed 15 digits')
            .regex(/^\d+$/, 'Phone number should only contain digits'),
          isVerified: z.boolean(),
        }),
      }),
      addressDetails: z.object({
        address1: z
          .string()
          .min(5, 'Address must be at least 5 characters')
          .max(200, 'Address must not exceed 200 characters'),
        address2: z
          .string()
          .max(200, 'Address must not exceed 200 characters')
          .nullable()
          .optional(),
        city: z
          .string()
          .min(2, 'City must be at least 2 characters')
          .max(100, 'City must not exceed 100 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'City should only contain letters and spaces'
          ),
        state: z
          .string()
          .min(2, 'State must be at least 2 characters')
          .max(100, 'State must not exceed 100 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'State should only contain letters and spaces'
          )
          .nullable()
          .optional(),
        country: z
          .string()
          .min(2, 'Country must be at least 2 characters')
          .max(100, 'Country must not exceed 100 characters')
          .regex(
            /^[A-Za-z\s]+$/,
            'Country should only contain letters and spaces'
          )
          .nullable()
          .optional(),
        postalCode: z
          .string()
          .min(4, 'Postal code must be at least 4 characters')
          .max(10, 'Postal code must not exceed 10 characters')
          .regex(/^[A-Za-z0-9\s-]+$/, 'Invalid postal code format'),
      }),
    }),
  });

const EditPersonalDetailsView: FC = () => {
  const { editedPersonalDetails, isLoading } = useProfilePersonalDetailsState();
  const { setEditedPersonalDetails } = useProfilePersonalDetailsActions();
  const isInitializedRef = useRef(false);
  const lastEditedDetailsIdRef = useRef<string | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<
    string | null
  >(null);

  const defaultValues: EditPersonalDetailsFormData = {
    basicDetails: {
      primaryDetails: {
        firstName: '',
        middleName: '',
        lastName: '',
        gender: 'select',
        profilePicture: null,
        emergencyContact: {
          firstName: '',
          middleName: '',
          lastName: '',
          gender: 'select',
          relationship: '',
          phone: {
            countryCode: '+1',
            phoneNumber: '',
            isVerified: false,
          },
          email: {
            email: '',
            isVerified: false,
          },
          address: null,
        },
      },
      contactDetails: {
        email: {
          email: '',
          isVerified: false,
        },
        phone: {
          countryCode: '+1',
          phoneNumber: '',
          isVerified: false,
        },
      },
      addressDetails: {
        address1: '',
        address2: null,
        city: '',
        state: null,
        country: null,
        postalCode: '',
      },
    },
  };

  const {
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<EditPersonalDetailsFormData>({
    resolver: zodResolver(editPersonalDetailsSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const profilePictureValue = watch(
    'basicDetails.primaryDetails.profilePicture'
  );

  const handleProfilePictureChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        return;
      }
      setValue('basicDetails.primaryDetails.profilePicture', file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const getProfilePictureUrl = () => {
    if (profilePicturePreview) {
      return profilePicturePreview;
    }
    if (profilePictureValue && typeof profilePictureValue === 'string') {
      return profilePictureValue;
    }
    const profilePicture =
      editedPersonalDetails?.basicDetails.primaryDetails.profile
        ?.profilePicture;
    return profilePicture?.thumbnailUrl || profilePicture?.fileUrl || '';
  };

  // Initialize form with editedPersonalDetails only once when it first loads
  useEffect(() => {
    if (editedPersonalDetails) {
      const currentId = editedPersonalDetails.id;
      // Only reset if this is a new editedPersonalDetails (different ID) or first time
      if (
        !isInitializedRef.current ||
        lastEditedDetailsIdRef.current !== currentId
      ) {
        const emergencyContact =
          editedPersonalDetails.basicDetails.primaryDetails.emergencyContact;
        const profilePicture =
          editedPersonalDetails.basicDetails.primaryDetails.profile
            ?.profilePicture;
        const profilePictureUrl =
          profilePicture?.thumbnailUrl || profilePicture?.fileUrl || '';

        const initialFormData = {
          basicDetails: {
            primaryDetails: {
              firstName:
                editedPersonalDetails.basicDetails.primaryDetails.firstName ||
                '',
              middleName:
                editedPersonalDetails.basicDetails.primaryDetails.middleName ||
                null,
              lastName:
                editedPersonalDetails.basicDetails.primaryDetails.lastName ||
                '',
              gender:
                editedPersonalDetails.basicDetails.primaryDetails.gender ||
                null,
              profilePicture: profilePictureUrl || null,
              emergencyContact: emergencyContact
                ? {
                  firstName: emergencyContact.firstName || '',
                  middleName: emergencyContact.middleName || null,
                  lastName: emergencyContact.lastName || '',
                  gender: emergencyContact.gender || null,
                  relationship: emergencyContact.relationship || '',
                  phone: {
                    countryCode: emergencyContact.phone?.countryCode || '+1',
                    phoneNumber: emergencyContact.phone?.phoneNumber || '',
                    isVerified: emergencyContact.phone?.isVerified || false,
                  },
                  email: {
                    email: emergencyContact.email?.email || '',
                    isVerified: emergencyContact.email?.isVerified || false,
                  },
                  address: emergencyContact.address
                    ? {
                      address1: emergencyContact.address.address1 || '',
                      address2: emergencyContact.address.address2 || null,
                      city: emergencyContact.address.city || '',
                      state: emergencyContact.address.state || null,
                      country: emergencyContact.address.country || null,
                      postalCode: emergencyContact.address.postalCode || '',
                    }
                    : null,
                }
                : {
                  firstName: '',
                  middleName: null,
                  lastName: '',
                  gender: null,
                  relationship: '',
                  phone: {
                    countryCode: '+1',
                    phoneNumber: '',
                    isVerified: false,
                  },
                  email: {
                    email: '',
                    isVerified: false,
                  },
                  address: null,
                },
            },
            contactDetails: {
              email: {
                email:
                  editedPersonalDetails.basicDetails.contactDetails.email
                    .email || '',
                isVerified:
                  editedPersonalDetails.basicDetails.contactDetails.email
                    .isVerified || false,
              },
              phone: {
                countryCode:
                  editedPersonalDetails.basicDetails.contactDetails.phone
                    .countryCode || '+1',
                phoneNumber:
                  editedPersonalDetails.basicDetails.contactDetails.phone
                    .phoneNumber || '',
                isVerified:
                  editedPersonalDetails.basicDetails.contactDetails.phone
                    .isVerified || false,
              },
            },
            addressDetails: {
              address1:
                editedPersonalDetails.basicDetails.addressDetails.address1 ||
                '',
              address2:
                editedPersonalDetails.basicDetails.addressDetails.address2 ||
                null,
              city:
                editedPersonalDetails.basicDetails.addressDetails.city || '',
              state:
                editedPersonalDetails.basicDetails.addressDetails.state || null,
              country:
                editedPersonalDetails.basicDetails.addressDetails.country ||
                null,
              postalCode:
                editedPersonalDetails.basicDetails.addressDetails.postalCode ||
                '',
            },
          },
        };

        reset(initialFormData as EditPersonalDetailsFormData);
        previousFormDataRef.current = JSON.stringify(
          initialFormData.basicDetails
        );
        setProfilePicturePreview(profilePictureUrl || null);
        isInitializedRef.current = true;
        lastEditedDetailsIdRef.current = currentId;
      }
    } else {
      isInitializedRef.current = false;
      lastEditedDetailsIdRef.current = null;
      previousFormDataRef.current = '';
    }
    // eslint-disable-next-line
  }, [editedPersonalDetails?.id, reset]);

  // Watch form changes and update context only when values actually change
  const formData = watch();
  const previousFormDataRef = useRef<string>('');

  useEffect(() => {
    if (
      !editedPersonalDetails ||
      !formData.basicDetails ||
      !isInitializedRef.current
    ) {
      return;
    }

    // Serialize current form data to compare (exclude profilePicture as it may be a File)
    const formDataForComparison = {
      ...formData.basicDetails,
      primaryDetails: {
        ...formData.basicDetails.primaryDetails,
        profilePicture:
          formData.basicDetails.primaryDetails.profilePicture instanceof File
            ? 'FILE_UPLOADED'
            : formData.basicDetails.primaryDetails.profilePicture,
      },
    };
    const currentFormDataString = JSON.stringify(formDataForComparison);

    // Only update if form data actually changed
    if (previousFormDataRef.current !== currentFormDataString) {
      previousFormDataRef.current = currentFormDataString;

      // Handle profile picture update
      let updatedProfile =
        editedPersonalDetails.basicDetails.primaryDetails.profile;
      if (formData.basicDetails.primaryDetails.profilePicture instanceof File) {
        // If a new file is uploaded, we'll need to handle it separately
        // For now, keep the existing profile picture structure
        // The actual upload will be handled by the API
        updatedProfile = {
          ...updatedProfile,
          profilePicture: updatedProfile?.profilePicture,
        };
      }

      const updatedDetails: IProfilePersonalDetailsApiResponse = {
        ...editedPersonalDetails,
        basicDetails: {
          ...editedPersonalDetails.basicDetails,
          primaryDetails: {
            ...editedPersonalDetails.basicDetails.primaryDetails,
            firstName: formData.basicDetails.primaryDetails.firstName,
            middleName: formData.basicDetails.primaryDetails.middleName,
            lastName: formData.basicDetails.primaryDetails.lastName,
            gender: formData.basicDetails.primaryDetails.gender,
            profile: updatedProfile,
            emergencyContact:
              formData.basicDetails.primaryDetails.emergencyContact ||
              undefined,
          },
          contactDetails: {
            ...editedPersonalDetails.basicDetails.contactDetails,
            ...formData.basicDetails.contactDetails,
          },
          addressDetails: {
            ...editedPersonalDetails.basicDetails.addressDetails,
            ...formData.basicDetails.addressDetails,
          },
        },
      };

      setEditedPersonalDetails(updatedDetails);
    }
  }, [formData, editedPersonalDetails, setEditedPersonalDetails]);

  if (!editedPersonalDetails && !isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>No personal details found</Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!editedPersonalDetails) {
    return null;
  }

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      data-testid="edit-personal-details-container"
      sx={{ py: { xs: 2, sm: 4 } }}
    >
      {/* Profile Picture Section */}
      <Grid item xs={12}>
        <Card variant="outlined" data-testid="profile-picture-card">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
              }}
            >
              <PhotoCamera color="primary" />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Profile Picture
              </Typography>
            </Box>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <Avatar
                src={getProfilePictureUrl()}
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                }}
                data-testid="profile-picture-avatar"
              >
                {!getProfilePictureUrl() && (
                  <Person sx={{ width: { xs: 50, sm: 60 }, height: { xs: 50, sm: 60 } }} />
                )}
              </Avatar>
              <Button
                component="label"
                variant="outlined"
                startIcon={<PhotoCamera />}
                sx={{
                  minHeight: { xs: 44, sm: 36 },
                  fontSize: { xs: '16px', sm: '14px' },
                }}
                data-testid="upload-profile-picture-button"
              >
                {profilePictureValue ? 'Change Picture' : 'Upload Picture'}
                <Input
                  type="file"
                  inputProps={{
                    accept: 'image/*',
                  }}
                  onChange={handleProfilePictureChange}
                  sx={{
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    height: 1,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    width: 1,
                  }}
                />
              </Button>
              {errors.basicDetails?.primaryDetails?.profilePicture && (
                <Typography variant="caption" color="error">
                  {errors.basicDetails.primaryDetails.profilePicture.message}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Primary Information */}
      <Grid item xs={12}>
        <Card variant="outlined" data-testid="primary-information-card">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
              }}
            >
              <Person color="primary" />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Primary Information
              </Typography>
            </Box>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  label="First Name"
                  name="basicDetails.primaryDetails.firstName"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.firstName?.message
                  }
                  id="first-name"
                  dataTestId="first-name-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  label="Middle Name"
                  name="basicDetails.primaryDetails.middleName"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.middleName?.message
                  }
                  id="middle-name"
                  dataTestId="middle-name-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  label="Last Name"
                  name="basicDetails.primaryDetails.lastName"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.lastName?.message
                  }
                  id="last-name"
                  dataTestId="last-name-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledSelectField
                  label="Gender"
                  id="gender"
                  name="basicDetails.primaryDetails.gender"
                  control={control}
                  itemList={[
                    { key: GENDER.MALE, value: 'Male' },
                    { key: GENDER.FEMALE, value: 'Female' },
                    { key: GENDER.OTHER, value: 'Other' },
                  ]}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.gender?.message
                  }
                  dataTestId="gender-field"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Contact Information */}
      <Grid item xs={12}>
        <Card variant="outlined" data-testid="contact-information-card">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
              }}
            >
              <Phone color="primary" />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Contact Information
              </Typography>
            </Box>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Email fontSize="small" color="action" />
                  <Typography variant="caption" color="textSecondary">
                    Email Address
                  </Typography>
                </Box>
                <ControlledTextField
                  label="Email"
                  name="basicDetails.contactDetails.email.email"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.contactDetails?.email?.email?.message
                  }
                  id="email"
                  dataTestId="email-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Phone fontSize="small" color="action" />
                  <Typography variant="caption" color="textSecondary">
                    Phone Number
                  </Typography>
                </Box>
                <ControlledTextField
                  label="Country Code"
                  name="basicDetails.contactDetails.phone.countryCode"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.contactDetails?.phone?.countryCode
                      ?.message
                  }
                  id="country-code"
                  dataTestId="country-code-field"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <ControlledTextField
                  label="Phone Number"
                  name="basicDetails.contactDetails.phone.phoneNumber"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.contactDetails?.phone?.phoneNumber
                      ?.message
                  }
                  id="phone-number"
                  dataTestId="phone-number-field"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Address Information */}
      <Grid item xs={12}>
        <Card variant="outlined" data-testid="address-information-card">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
              }}
            >
              <LocationOn color="primary" />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Address Information
              </Typography>
            </Box>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12}>
                <ControlledTextField
                  label="Address Line 1"
                  name="basicDetails.addressDetails.address1"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.addressDetails?.address1?.message
                  }
                  id="address1"
                  dataTestId="address1-field"
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  label="Address Line 2"
                  name="basicDetails.addressDetails.address2"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.addressDetails?.address2?.message
                  }
                  id="address2"
                  dataTestId="address2-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="City"
                  name="basicDetails.addressDetails.city"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.addressDetails?.city?.message
                  }
                  id="city"
                  dataTestId="city-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="State"
                  name="basicDetails.addressDetails.state"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.addressDetails?.state?.message
                  }
                  id="state"
                  dataTestId="state-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="Country"
                  name="basicDetails.addressDetails.country"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.addressDetails?.country?.message
                  }
                  id="country"
                  dataTestId="country-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="Postal Code"
                  name="basicDetails.addressDetails.postalCode"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.addressDetails?.postalCode?.message
                  }
                  id="postal-code"
                  dataTestId="postal-code-field"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Emergency Contact Information */}
      <Grid item xs={12}>
        <Card variant="outlined" data-testid="emergency-contact-card">
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
              }}
            >
              <ContactEmergency color="primary" />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                Emergency Contact
              </Typography>
            </Box>
            <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  label="First Name"
                  name="basicDetails.primaryDetails.emergencyContact.firstName"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.firstName?.message
                  }
                  id="emergency-first-name"
                  dataTestId="emergency-first-name-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  label="Middle Name"
                  name="basicDetails.primaryDetails.emergencyContact.middleName"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.middleName?.message
                  }
                  id="emergency-middle-name"
                  dataTestId="emergency-middle-name-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  label="Last Name"
                  name="basicDetails.primaryDetails.emergencyContact.lastName"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.lastName?.message
                  }
                  id="emergency-last-name"
                  dataTestId="emergency-last-name-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledSelectField
                  label="Gender"
                  id="emergency-gender"
                  name="basicDetails.primaryDetails.emergencyContact.gender"
                  control={control}
                  itemList={[
                    { key: GENDER.MALE, value: 'Male' },
                    { key: GENDER.FEMALE, value: 'Female' },
                    { key: GENDER.OTHER, value: 'Other' },
                  ]}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.gender?.message
                  }
                  dataTestId="emergency-gender-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <ControlledTextField
                  label="Relationship"
                  name="basicDetails.primaryDetails.emergencyContact.relationship"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.relationship?.message
                  }
                  id="emergency-relationship"
                  dataTestId="emergency-relationship-field"
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Email fontSize="small" color="action" />
                  <Typography variant="caption" color="textSecondary">
                    Email Address
                  </Typography>
                </Box>
                <ControlledTextField
                  label="Email"
                  name="basicDetails.primaryDetails.emergencyContact.email.email"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact?.email
                      ?.email?.message
                  }
                  id="emergency-email"
                  dataTestId="emergency-email-field"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Phone fontSize="small" color="action" />
                  <Typography variant="caption" color="textSecondary">
                    Phone Number
                  </Typography>
                </Box>
                <ControlledTextField
                  label="Country Code"
                  name="basicDetails.primaryDetails.emergencyContact.phone.countryCode"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact?.phone
                      ?.countryCode?.message
                  }
                  id="emergency-country-code"
                  dataTestId="emergency-country-code-field"
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <ControlledTextField
                  label="Phone Number"
                  name="basicDetails.primaryDetails.emergencyContact.phone.phoneNumber"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact?.phone
                      ?.phoneNumber?.message
                  }
                  id="emergency-phone-number"
                  dataTestId="emergency-phone-number-field"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Address (Optional)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  label="Address Line 1"
                  name="basicDetails.primaryDetails.emergencyContact.address.address1"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.address?.address1?.message
                  }
                  id="emergency-address1"
                  dataTestId="emergency-address1-field"
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  label="Address Line 2"
                  name="basicDetails.primaryDetails.emergencyContact.address.address2"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.address?.address2?.message
                  }
                  id="emergency-address2"
                  dataTestId="emergency-address2-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="City"
                  name="basicDetails.primaryDetails.emergencyContact.address.city"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.address?.city?.message
                  }
                  id="emergency-city"
                  dataTestId="emergency-city-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="State"
                  name="basicDetails.primaryDetails.emergencyContact.address.state"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.address?.state?.message
                  }
                  id="emergency-state"
                  dataTestId="emergency-state-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="Country"
                  name="basicDetails.primaryDetails.emergencyContact.address.country"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.address?.country?.message
                  }
                  id="emergency-country"
                  dataTestId="emergency-country-field"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  label="Postal Code"
                  name="basicDetails.primaryDetails.emergencyContact.address.postalCode"
                  control={control}
                  errorMessage={
                    errors.basicDetails?.primaryDetails?.emergencyContact
                      ?.address?.postalCode?.message
                  }
                  id="emergency-postal-code"
                  dataTestId="emergency-postal-code-field"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default EditPersonalDetailsView;
